import { TabData } from '@ddt-web/data-service';
import { NumberUtil, ObjectUtil } from '@ddt-web/generic-utils';

import { ChartFactory } from '../factories';
import { IChartData } from '../models/ichart-data-model.interface';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

const DEFAULT_PIE_CHART_CONFIG: any = {
	plotOptions: {
		pie: {
			dataLabels: {
				enabled: false
			},
			innerSize: '80%',
			startAngle: 0,
			endAngle: 360,
			center: ['50%', '50%']
		}
	},
	tooltip: {
		formatter: function (): string {
			return this.point.name + ' <b>' + this.point.y + '%';
		},
		backgroundColor: '#f9f9f9',
		borderColor: '#a5a5a5',
		borderRadius: 0,
		shadow: false,
		useHTML: true,
		style: {
			fontFamily: '"FortBook", Arial, sans-serif'
		}
	},
	title: {
		align: 'center',
		verticalAlign: 'middle',
		y: -15
	}
};

export class DoughnutChartConfig extends DefaultChartConfig {
	private seriesTitle: string;
	private chartData: IChartData;
	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: DoughtNutChartCustomAttributes
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration.chart.type = ChartType.Pie;
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_PIE_CHART_CONFIG);
		if (this.data instanceof TabData) {
			this.chartData = ChartFactory.getStackedPieData(this.data, this.dataIndex);
		}
	}

	public render(): void {
		this.updateDefaultConfiguration();

		switch (this.name) {
			case 'areas-to-explore':
				this.configuration.tooltip = {
					formatter: function (): string {
						return this.point.name + ' <b>' + NumberUtil.numberFormatter(this.point.y, '%');
					},
					backgroundColor: '#f9f9f9',
					borderColor: '#a5a5a5',
					borderRadius: 0,
					shadow: false,
					useHTML: true,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif'
					}
				};

				this.configuration.title.text = this.title ? this.title : null;
				this.renderDefaultChart();
				break;
			case 'exposure':
				this.configuration.tooltip.pointFormat =
					'<div class="asset-allocation-tooltip row text-12"><span class="col-15">{point.category}</span><span class="col-9 text-align-right">{point.formattedY}</span></div>';
				this.renderDefaultChart();
				break;
			default:
				this.renderDefaultChart();
				break;
		}
	}

	public getChartConfig(): any {
		return ObjectUtil.extend(this.configuration, {
			colors: this.chartData.colors,
			series: [
				{
					name: this.chartData.profileName,
					data: this.chartData.series
				}
			]
		});
	}

	private updateDefaultConfiguration(): any {
		const donutMaxWidth = 200;
		const containerWidth = this.container.offsetWidth;
		const chartHeight = this.height ? this.height : containerWidth <= donutMaxWidth ? containerWidth : donutMaxWidth;
		const chartWidth = this.width ? this.width : containerWidth <= donutMaxWidth ? containerWidth : donutMaxWidth;
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				height: chartHeight,
				width: chartWidth
			},
			colors: this.chartData.colors,
			responsive: {
				rules: [
					{
						condition: { maxWidth: 140 },
						chartOptions: {
							chart: {
								height: chartHeight + 60,
								spacingBottom: 60
							},
							plotOptions: {
								pie: {
									innerSize: '55%'
								}
							},
							title: {
								verticalAlign: 'bottom',
								widthAdjust: -20,
								y: 10,
								style: {
									fontSize: '12px',
									lineHeight: '16px'
								}
							}
						}
					},
					{
						condition: { maxWidth: 90 },
						chartOptions: {
							chart: {
								height: chartHeight + 90,
								spacingBottom: 90
							},
							plotOptions: {
								pie: {
									innerSize: '0%'
								}
							},
							title: {
								widthAdjust: -10
							}
						}
					}
				]
			},
			title: {
				text: this.hideTitle ? '' : this.seriesTitle,
				widthAdjust: this.width ? (this.width >= 175 ? -100 : -50) : containerWidth >= 175 ? -100 : -50,
				style: {
					fontSize: this.width ? (this.width >= 200 ? '14px' : '11px') : containerWidth >= 200 ? '14px' : '11px',
					lineHeight: this.width ? (this.width >= 200 ? '18px' : '13px') : containerWidth >= 200 ? '18px' : '13px'
				}
			},
			series: [{ name: this.chartData.profileName, data: this.chartData.series }]
		});
	}

	private updateDefaultConfigurationForPDF(): any {
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				// TODO: height and width value should be dynamic
				height: 75,
				width: 75
			},
			credits: {
				enabled: false
			},
			title: {
				text: null
			},
			plotOptions: {
				pie: {
					size: '100%',
					innerSize: '70%',
					dataLabels: {
						enabled: false
					},
					slicedOffset: 0
				}
			},
			colors: this.chartData.colors,
			series: [{ name: this.chartData.profileName, data: this.chartData.series }]
		});
	}

	public getConfigurationForPDF(): any {
		this.updateDefaultConfigurationForPDF();
		return this.configuration;
	}

	private renderDefaultChart(): void {
		Highcharts.chart(this.configuration);
	}
}
export interface DoughtNutChartCustomAttributes {
	// template interface for now in case someone need manual config on these chart
	[id: string]: any;
}
